import { useState, useEffect } from 'react'
import { useApp, useAuth }     from '../components/AppContext'
import { calcTax, fmt, mileageAllowance, homeOfficeAllowance, getCurrentQuarter } from '../lib/tax'
import { exportTransactionsCSV, exportPLSummary, exportAccountantPack } from '../lib/export'
import { CheckCircle, Send, Shield, Lock, AlertTriangle, Download, FileText, Package, Link, Clock, ChevronRight, Info, XCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getDeviceId } from '../lib/device'
import { useNavigate } from 'react-router-dom'

const QUARTER_PERIODS = {
  Q1: { start:'2026-04-06', end:'2026-07-05', label:'6 Apr – 5 Jul 2026', deadline:'7 Aug 2026' },
  Q2: { start:'2026-07-06', end:'2026-10-05', label:'6 Jul – 5 Oct 2026', deadline:'7 Nov 2026' },
  Q3: { start:'2026-10-06', end:'2027-01-05', label:'6 Oct – 5 Jan 2027', deadline:'7 Feb 2027' },
  Q4: { start:'2027-01-06', end:'2027-04-05', label:'6 Jan – 5 Apr 2027', deadline:'7 May 2027' },
}

export default function Submit() {
  const { transactions, mileageTrips, homeOffice, lockPeriod, isPeriodLocked, saveSubmission } = useApp()
  const { user, profile, saveProfile } = useAuth()
  const navigate = useNavigate()

  const [step,            setStep]           = useState(0)
  const [submitting,      setSubmitting]     = useState(false)
  const [submitError,     setSubmitError]    = useState('')
  const [hmrcRef,         setHmrcRef]        = useState('')
  const [pastSubmissions, setPastSubmissions] = useState([])
  const [loadingHistory,  setLoadingHistory]  = useState(true)
  const [selectedQuarter, setSelectedQuarter] = useState(null)

  const quarter       = getCurrentQuarter()
  const hmrcConnected = profile?.hmrc_connected === true
  const isDemo        = !hmrcConnected

  useEffect(() => {
    if (!user) return
    loadHistory()
  }, [user])

  async function loadHistory() {
    setLoadingHistory(true)
    const { data } = await supabase
      .from('submissions')
      .select('*')
      .eq('user_id', user.id)
      .order('submitted_at', { ascending: false })
    if (data) setPastSubmissions(data)
    setLoadingHistory(false)
  }

  const activeQuarterKey    = selectedQuarter || quarter.label
  const activeQuarterPeriod = QUARTER_PERIODS[activeQuarterKey] || QUARTER_PERIODS[quarter.label]

  const quarterTx = transactions.filter(t => {
    if (!activeQuarterPeriod) return false
    const d = new Date(t.date)
    return d >= new Date(activeQuarterPeriod.start) && d <= new Date(activeQuarterPeriod.end)
  })

  const quarterMiles = mileageTrips.filter(t => {
    if (!activeQuarterPeriod) return false
    const d = new Date(t.date)
    return d >= new Date(activeQuarterPeriod.start) && d <= new Date(activeQuarterPeriod.end)
  })

  const income   = quarterTx.filter(t => t.type==='income'  && t.category && t.category!=='personal').reduce((s,t)=>s+t.amount,0)
  const expenses = quarterTx.filter(t => t.type==='expense' && t.category && t.category!=='personal').reduce((s,t)=>s+t.amount,0)
  const miles    = quarterMiles.reduce((s,t)=>s+Number(t.miles),0)
  const mileAdj  = mileageAllowance(miles)
  const homeAdj  = homeOfficeAllowance(homeOffice.months, homeOffice.hoursPerMonth)
  const totalExp = expenses + mileAdj + homeAdj
  const qProfit  = Math.max(0, income - totalExp)
  // Annualise quarterly profit to calculate correct full-year Income Tax + NI
  const annualisedProfit = qProfit * 4
  const { incomeTax, ni, total } = calcTax(annualisedProfit, 0)
  const belowAllowance = annualisedProfit > 0 && annualisedProfit <= 12570

  const uncatCount   = quarterTx.filter(t => !t.category).length
  const receiptsUp   = quarterTx.filter(t => t.has_vat_receipt).length
  const expCount     = quarterTx.filter(t => t.type==='expense').length
  const alreadyFiled = isPeriodLocked && isPeriodLocked(activeQuarterKey)

  const filedQuarters = pastSubmissions
    .filter(s => s.status==='submitted' || s.status==='accepted')
    .map(s => s.quarter)

  // ── Real HMRC submission ──────────────────────────────────────────────────
  async function submitToHMRC() {
    // HMRC require submissions to be derived from a complete digital record.
    // Nothing unreviewed may reach them, so this is enforced here as well as
    // in the UI, in case the transaction set changes mid-flow.
    if (uncatCount > 0) {
      setSubmitError(`${uncatCount} transaction${uncatCount === 1 ? '' : 's'} in ${activeQuarterKey} still need categorising. Categorise them before filing.`)
      return
    }
    setSubmitting(true)
    setSubmitError('')
    try {
      // HMRC fraud prevention requires the client's public (ephemeral) port.
      // Browsers only expose this via a WebRTC STUN candidate.
      let clientPublicPort = ''
      await new Promise(resolve => {
        try {
          const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] })
          pc.createDataChannel('')
          pc.createOffer().then(o => pc.setLocalDescription(o))
          pc.onicecandidate = e => {
            const m = e.candidate?.candidate?.match(/udp\s+\d+\s+[\d.]+\s+(\d+)/i)
            if (m) { clientPublicPort = m[1]; pc.close(); resolve() }
          }
          setTimeout(resolve, 3000)
        } catch { resolve() }
      })

      const res = await fetch('/api/hmrc-submit', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken:         profile.hmrc_access_token,
          refreshToken:        profile.hmrc_refresh_token,
          tokenExpiry:         profile.hmrc_token_expiry,
          nino:                profile.hmrc_nino,       // National Insurance number
          businessId:          profile.hmrc_business_id, // MTD IT ID / income source ID
          income,
          expenses,
          mileageAllowance:    mileAdj,
          homeOfficeAllowance: homeAdj,
          periodStart:         activeQuarterPeriod.start,
          periodEnd:           activeQuarterPeriod.end,
          userAgent:           navigator.userAgent,
          timezone:            Intl.DateTimeFormat().resolvedOptions().timeZone,
          screens:             `width=${screen.width}&height=${screen.height}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${screen.colorDepth}`,
          // outerWidth/outerHeight are in screen coordinates and unaffected by
          // page zoom, so the window is never reported as wider than the screen.
          windowSize:          `width=${window.outerWidth}&height=${window.outerHeight}`,
          plugins:             Array.from(navigator.plugins || []).map(p => p.name).join(',').substring(0, 500),
          doNotTrack:          navigator.doNotTrack || null,
          localIPs:            '',
          userId:              user?.id,
          deviceId:            getDeviceId(),
          clientPublicPort,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setSubmitError(data.error || 'HMRC rejected the submission. Please check your figures and try again.')
        setSubmitting(false)
        return
      }

      if (data.tokenRefreshed) {
        await saveProfile({
          hmrc_access_token:  data.newAccessToken,
          hmrc_refresh_token: data.newRefreshToken,
          hmrc_token_expiry:  data.newExpiryTime,
        })
      }

      const ref = data.submissionId || 'MTD-' + Date.now()
      setHmrcRef(ref)

      await saveSubmission({
        quarter:               activeQuarterKey,
        tax_year:              '2025-26',
        income,
        expenses:              totalExp,
        profit:                qProfit,
        status:                'submitted',
        hmrc_ref:              ref,
        hmrc_submission_id:    data.submissionId || null,
        period_start:          activeQuarterPeriod.start,
        period_end:            activeQuarterPeriod.end,
        mileage_allowance:     mileAdj,
        home_office_allowance: homeAdj,
        submitted_at:          new Date().toISOString(),
      })

      lockPeriod(activeQuarterKey)
      await loadHistory()
      setStep(3)
    } catch (err) {
      console.error('Submit error:', err)
      setSubmitError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Demo submission ───────────────────────────────────────────────────────
  async function submitDemo() {
    setSubmitting(true)
    await new Promise(r => setTimeout(r, 2000))
    const ref = 'DEMO-' + Math.random().toString(36).slice(2,10).toUpperCase()
    setHmrcRef(ref)
    lockPeriod(activeQuarterKey)
    setSubmitting(false)
    setStep(3)
  }

  // ── Export panel ──────────────────────────────────────────────────────────
  const ExportPanel = () => (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ fontWeight:600, fontSize:14, marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
        <Download size={15} color="var(--teal)" /> Export & Reports
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {[
          { icon:FileText, label:'Transaction CSV',  sub:'All transactions with categories and HMRC rules',         fn:()=>exportTransactionsCSV(transactions,mileageTrips,homeOffice) },
          { icon:FileText, label:'P&L Summary CSV',  sub:'Income, expenses and tax estimate by category',           fn:()=>exportPLSummary(transactions,mileageTrips,homeOffice) },
          { icon:Package,  label:'Accountant Pack',  sub:'Full handoff pack — data quality report, transaction log', fn:()=>exportAccountantPack(transactions,mileageTrips,homeOffice,profile) },
        ].map(item => (
          <button key={item.label} onClick={item.fn} className="btn btn-ghost btn-sm"
            style={{ justifyContent:'flex-start', gap:12, padding:'10px 14px', height:'auto', textAlign:'left', width:'100%' }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'var(--teal-glow)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <item.icon size={14} color="var(--teal)" />
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--white)' }}>{item.label}</div>
              <div style={{ fontSize:11, color:'var(--slate)', marginTop:1 }}>{item.sub}</div>
            </div>
            <Download size={13} color="var(--slate)" style={{ marginLeft:'auto', flexShrink:0 }} />
          </button>
        ))}
      </div>
    </div>
  )

  // ── Submission history ────────────────────────────────────────────────────
  const SubmissionHistory = () => {
    if (loadingHistory || !pastSubmissions.length) return null
    return (
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ fontWeight:600, fontSize:14, marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
          <Clock size={15} color="var(--teal)" /> Submission History
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {pastSubmissions.map(s => {
            const period = QUARTER_PERIODS[s.quarter]
            return (
              <div key={s.id} style={{ padding:'12px 14px', background:'rgba(255,255,255,.02)', borderRadius:8, border:'1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <CheckCircle size={13} color="var(--teal)" />
                    <span style={{ fontSize:13, fontWeight:600 }}>{s.quarter} · {s.tax_year}</span>
                  </div>
                  <span style={{ fontSize:11, color:'var(--slate-dim)' }}>
                    {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : ''}
                  </span>
                </div>
                {period && <div style={{ fontSize:11, color:'var(--slate)', marginBottom:8 }}>Covered: {period.label}</div>}
                <div style={{ display:'flex', gap:16 }}>
                  {[
                    { label:'Income',   value: fmt(s.income) },
                    { label:'Expenses', value: fmt(s.expenses) },
                    { label:'Profit',   value: fmt(s.profit) },
                  ].map(r => (
                    <div key={r.label}>
                      <div style={{ fontSize:10, color:'var(--slate-dim)', textTransform:'uppercase', letterSpacing:'.06em' }}>{r.label}</div>
                      <div style={{ fontSize:12, fontWeight:600 }}>{r.value}</div>
                    </div>
                  ))}
                </div>
                {s.hmrc_ref && (
                  <div style={{ marginTop:8, padding:'3px 8px', background:'var(--teal-glow)', borderRadius:5, display:'inline-block' }}>
                    <span style={{ fontSize:10, color:'var(--teal)', fontFamily:'monospace' }}>
                      {s.hmrc_ref.startsWith('DEMO') ? 'Demo ref: ' : 'HMRC ref: '}{s.hmrc_ref}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Next quarter info ─────────────────────────────────────────────────────
  const NextQuarterInfo = () => {
    const lastFiled = pastSubmissions.find(s => s.status==='submitted' || s.status==='accepted')
    if (!lastFiled) return null
    const lastPeriod = QUARTER_PERIODS[lastFiled.quarter]
    const nextKey    = { Q1:'Q2', Q2:'Q3', Q3:'Q4', Q4:'Q1' }[lastFiled.quarter]
    const nextPeriod = QUARTER_PERIODS[nextKey]
    if (!nextPeriod) return null
    return (
      <div style={{ background:'rgba(0,212,170,.04)', border:'1px solid rgba(0,212,170,.15)', borderRadius:10, padding:'14px 16px', marginBottom:16, display:'flex', gap:12 }}>
        <Info size={16} color="var(--teal)" style={{ flexShrink:0, marginTop:1 }} />
        <div>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>{nextKey} is your next quarter</div>
          <div style={{ fontSize:12, color:'var(--slate)', lineHeight:1.6 }}>
            You filed up to <strong style={{color:'var(--white)'}}>{new Date(lastPeriod.end).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</strong>. All transactions from <strong style={{color:'var(--teal)'}}>{new Date(nextPeriod.start).toLocaleDateString('en-GB',{day:'numeric',month:'long'})}</strong> onwards go into <strong style={{color:'var(--white)'}}>{nextKey}</strong>, due <strong style={{color:'var(--white)'}}>{nextPeriod.deadline}</strong>.
          </div>
        </div>
      </div>
    )
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (step === 3) return (
    <div className="fade-up" style={{ maxWidth:560, margin:'0 auto' }}>
      <div style={{ textAlign:'center', padding:'40px 0 24px' }}>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--teal-glow)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
          <CheckCircle size={32} color="var(--teal)" />
        </div>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:26, marginBottom:8 }}>
          {isDemo ? 'Demo submission complete' : `${activeQuarterKey} submitted to HMRC`}
        </h1>
        <p style={{ color:'var(--slate)', fontSize:13, marginBottom:16 }}>
          {isDemo
            ? 'Nothing was sent to HMRC. Connect your HMRC account to file for real.'
            : `Your ${activeQuarterKey} quarterly update has been received by HMRC.`}
        </p>
        <div style={{ background:'var(--teal-glow)', border:'1px solid rgba(0,212,170,.2)', borderRadius:8, padding:'8px 16px', display:'inline-block', fontFamily:'monospace', color:'var(--teal)', margin:'8px 0 4px', fontSize:13 }}>
          {hmrcRef}
        </div>
        <div style={{ fontSize:11, color:'var(--slate-dim)', marginBottom:24 }}>
          {isDemo ? 'Demo reference — not a real HMRC reference' : 'HMRC submission reference — keep this for your records'}
        </div>
        <div className="card" style={{ textAlign:'left', marginBottom:16 }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>What was submitted — {activeQuarterKey}</div>
          <div style={{ fontSize:11, color:'var(--slate)', marginBottom:12 }}>{activeQuarterPeriod?.label}</div>
          {[
            { label:'Income',                 value: fmt(income) },
            { label:'Business expenses',      value: fmt(expenses) },
            { label:'Mileage allowance',      value: fmt(mileAdj) },
            { label:'Home office',            value: fmt(homeAdj) },
            { label:'Total deductions',       value: fmt(totalExp) },
            { label:'Net profit',             value: fmt(qProfit),  bold:true },
            { label:'Income Tax due',         value: fmt(incomeTax) },
            { label:'NI Class 2 & 4 due',     value: fmt(ni) },
            { label:'Total tax this quarter', value: fmt(total),    bold:true },
          ].map(r => (
            <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
              <span style={{ fontSize:12, color:'var(--slate)' }}>{r.label}</span>
              <span style={{ fontSize:12, fontWeight: r.bold ? 700 : 500, color: r.bold ? 'var(--teal)' : 'var(--white)' }}>{r.value}</span>
            </div>
          ))}
        </div>
        <div style={{ background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.2)', borderRadius:'var(--r)', padding:'14px 18px', textAlign:'left', marginBottom:20, display:'flex', gap:12 }}>
          <Lock size={18} color="var(--teal)" style={{ flexShrink:0, marginTop:1 }} />
          <div>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:3 }}>{activeQuarterKey} is now locked</div>
            <div style={{ fontSize:12, color:'var(--slate)' }}>Prior-period data is protected. Any amendments will be tracked separately.</div>
          </div>
        </div>
        {!isDemo && <NextQuarterInfo />}
        <ExportPanel />
        {isDemo && (
          <button className="btn btn-primary btn-full" onClick={() => navigate('/settings?tab=integrations')} style={{ gap:8, marginTop:8 }}>
            <Link size={14} /> Connect HMRC to file for real
          </button>
        )}
      </div>
    </div>
  )

  const steps = ['Review', 'Check', 'Confirm', 'Done']

  return (
    <div className="fade-up">
      <div className="page-head">
        <h1 className="page-title">MTD Submit</h1>
        <p className="page-sub">{activeQuarterKey} 2025/26 · Deadline: {activeQuarterPeriod?.deadline}</p>
      </div>

      {isDemo && (
        <div style={{
          background:'repeating-linear-gradient(45deg,rgba(245,166,35,.06),rgba(245,166,35,.06) 10px,transparent 10px,transparent 20px)',
          border:'2px solid rgba(245,166,35,.4)', borderRadius:'var(--r-sm)', padding:'10px 16px',
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:20, flexWrap:'wrap',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <AlertTriangle size={15} color="var(--amber)" style={{ flexShrink:0 }} />
            <div>
              <span style={{ fontSize:12, fontWeight:700, color:'var(--amber)' }}>DEMO MODE — </span>
              <span style={{ fontSize:12, color:'var(--slate)' }}>Nothing will be sent to HMRC. Connect your account to file for real.</span>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ gap:6, flexShrink:0 }}
            onClick={() => navigate('/settings?tab=integrations')}>
            Connect HMRC
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Which quarter are you filing?</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {Object.keys(QUARTER_PERIODS).map(q => {
            const filed = filedQuarters.includes(q)
            return (
              <button key={q}
                onClick={() => { if (!filed) { setSelectedQuarter(q); setStep(0); setSubmitError('') } }}
                className="btn btn-sm"
                style={{
                  background: activeQuarterKey===q ? 'var(--teal-glow)' : 'var(--navy-border)',
                  border:`1px solid ${activeQuarterKey===q ? 'rgba(0,212,170,.4)' : 'transparent'}`,
                  color: filed ? 'var(--slate-dim)' : activeQuarterKey===q ? 'var(--teal)' : 'var(--slate)',
                  cursor: filed ? 'default' : 'pointer', gap:5,
                }}>
                {filed && <CheckCircle size={11} />}{q}{filed ? ' · Filed' : ''}
              </button>
            )
          })}
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:24, overflowX:'auto' }}>
        {steps.map((s, i) => (
          <div key={s} style={{ display:'flex', alignItems:'center' }}>
            <div style={{
              display:'flex', alignItems:'center', gap:7, padding:'6px 12px', borderRadius:20,
              background: i===step ? 'var(--teal-glow)' : i<step ? 'rgba(0,212,170,.08)' : 'transparent',
              border:`1px solid ${i===step ? 'rgba(0,212,170,.4)' : i<step ? 'rgba(0,212,170,.2)' : 'var(--navy-border)'}`,
            }}>
              <div style={{
                width:20, height:20, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700,
                background: i<step ? 'var(--teal)' : i===step ? 'rgba(0,212,170,.3)' : 'var(--navy-border)',
                color: i<step ? 'var(--navy)' : 'var(--white)',
              }}>
                {i < step ? '✓' : i+1}
              </div>
              <span style={{ fontSize:12, color:i===step?'var(--teal)':i<step?'var(--slate)':'var(--slate-dim)', whiteSpace:'nowrap' }}>{s}</span>
            </div>
            {i < steps.length-1 && <div style={{ width:20, height:1, background:'var(--navy-border)', flexShrink:0 }} />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="fade-in">
          {alreadyFiled && (
            <div className="alert alert-teal" style={{ marginBottom:16 }}>
              <CheckCircle size={14} />
              <span>{activeQuarterKey} has already been filed. See history below.</span>
            </div>
          )}
          {uncatCount > 0 && (
            <div className="alert alert-red" style={{ marginBottom:16, alignItems:'flex-start' }}>
              <XCircle size={15} style={{ flexShrink:0, marginTop:2 }} />
              <div>
                <div style={{ marginBottom:6 }}>
                  <strong>{uncatCount} transaction{uncatCount === 1 ? '' : 's'} still need categorising.</strong>
                </div>
                <div style={{ fontSize:12, lineHeight:1.6, marginBottom:10 }}>
                  Everything in {activeQuarterKey} must be categorised before you can file. HMRC require
                  quarterly updates to be derived from a complete digital record, so Quarterly will not
                  submit figures while transactions are unreviewed.
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('/categorise')}>
                  Categorise now <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
          {expCount > 0 && receiptsUp < expCount * 0.4 && (
            <div className="alert alert-amber" style={{ marginBottom:16 }}>
              <AlertTriangle size={15} />
              <span><strong>Low receipt coverage ({expCount > 0 ? Math.round(receiptsUp/expCount*100) : 0}%).</strong> You may miss VAT reclaims.</span>
            </div>
          )}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Quarter Summary — {activeQuarterKey}</div>
            <div style={{ fontSize:11, color:'var(--slate)', marginBottom:14 }}>{activeQuarterPeriod?.label}</div>
            {[
              { label:'Total income',             value:fmt(income),   color:'var(--teal)' },
              { label:'Business expenses',        value:fmt(expenses), color:'var(--white)' },
              { label:'Mileage allowance (HMRC)', value:fmt(mileAdj),  color:'var(--slate)' },
              { label:'Home office allowance',    value:fmt(homeAdj),  color:'var(--slate)' },
              { label:'Total allowable expenses', value:fmt(totalExp), color:'var(--white)' },
              { label:'Net profit',               value:fmt(qProfit),  color:'var(--white)', bold:true },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize:13, color:'var(--slate)' }}>{row.label}</span>
                <span style={{ fontSize:13, fontWeight:row.bold?700:600, color:row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Data Quality</div>
            <div style={{ fontSize:12, color:'var(--slate)', marginBottom:12 }}>Transactions in this quarter: {quarterTx.length}</div>
            {[
              { label:'Transactions categorised', value:`${quarterTx.length-uncatCount} / ${quarterTx.length}`, ok:uncatCount===0 },
              { label:'Receipts uploaded',        value:`${receiptsUp} / ${expCount}`,                           ok:expCount===0||receiptsUp>=expCount*0.5 },
              { label:'Mileage logged',           value:`${quarterMiles.length} trips · ${miles.toFixed(0)} mi`, ok:quarterMiles.length>0 },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {row.ok ? <CheckCircle size={13} color="var(--teal)" /> : <AlertTriangle size={13} color="var(--amber)" />}
                  <span style={{ fontSize:13, color:row.ok?'var(--white)':'var(--slate)' }}>{row.label}</span>
                </div>
                <span style={{ fontSize:12, color:'var(--slate-dim)' }}>{row.value}</span>
              </div>
            ))}
          </div>
          <ExportPanel />
          <SubmissionHistory />
          <button
            className="btn btn-primary btn-full btn-lg"
            onClick={() => setStep(1)}
            disabled={alreadyFiled || uncatCount > 0}
          >
            {alreadyFiled
              ? 'Already filed'
              : uncatCount > 0
                ? `Categorise ${uncatCount} transaction${uncatCount === 1 ? '' : 's'} to continue`
                : <>Continue to check <ChevronRight size={15} /></>}
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="fade-in">
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Estimated full-year tax — {activeQuarterKey}</div>
            <div style={{ fontSize:11, color:'var(--slate)', marginBottom:16 }}>{activeQuarterPeriod?.label} · {fmt(qProfit)} quarterly profit × 4 = {fmt(annualisedProfit)} annualised</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:36, color:'var(--amber)', marginBottom:4 }}>{fmt(total)}</div>
            <div style={{ fontSize:11, color:'var(--slate-dim)', marginBottom:16 }}>Projected full-year Income Tax + NI</div>

            {belowAllowance && (
              <div className="alert" style={{ marginBottom:16, background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.2)', borderRadius:8 }}>
                <Shield size={14} color="var(--teal)" style={{ flexShrink:0 }} />
                <span style={{ fontSize:12, color:'var(--slate)' }}>
                  Your projected annual profit of {fmt(annualisedProfit)} is below the £12,570 personal allowance — no Income Tax or NI is due at this level. This will update automatically as you categorise more transactions.
                </span>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:16 }}>
              {[
                { label:'Income Tax',     value:fmt(incomeTax), sub:'Full-year estimate' },
                { label:'NI Class 2 & 4', value:fmt(ni),        sub:'Class 2 + Class 4 combined' },
              ].map(r => (
                <div key={r.label} style={{ background:'rgba(255,255,255,.04)', borderRadius:8, padding:'12px 14px' }}>
                  <div style={{ fontSize:11, color:'var(--slate)', marginBottom:2 }}>{r.label}</div>
                  <div style={{ fontSize:18, fontWeight:700, marginBottom:3 }}>{r.value}</div>
                  <div style={{ fontSize:10, color:'var(--slate-dim)' }}>{r.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ borderTop:'1px solid rgba(255,255,255,.06)', paddingTop:12 }}>
              {[
                { label:'Income',   value:fmt(income) },
                { label:'Expenses', value:fmt(totalExp) },
                { label:'Profit',   value:fmt(qProfit) },
              ].map(r => (
                <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0' }}>
                  <span style={{ fontSize:12, color:'var(--slate)' }}>{r.label}</span>
                  <span style={{ fontSize:12, fontWeight:500 }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="alert alert-teal" style={{ marginBottom:16 }}>
            <Shield size={15} />
            <span style={{ fontSize:12 }}>Calculated using HMRC 2026/27 rates. Figures are annualised from this quarter's profit. Meals & Entertainment capped at 50% automatically.</span>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-ghost btn-full" onClick={() => setStep(0)}>Back</button>
            <button className="btn btn-primary btn-full btn-lg" onClick={() => setStep(2)}>Confirm figures</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="fade-in">
          {hmrcConnected ? (
            <div className="card" style={{ marginBottom:16, border:'1px solid rgba(0,212,170,.3)', background:'rgba(0,212,170,.04)' }}>
              <div style={{ display:'flex', gap:12 }}>
                <CheckCircle size={20} color="var(--teal)" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'var(--teal)', marginBottom:4 }}>Live HMRC submission</div>
                  <div style={{ fontSize:13, color:'var(--slate)' }}>This will submit your {activeQuarterKey} quarterly update directly to HMRC via Making Tax Digital.</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom:16, border:'2px solid rgba(245,166,35,.4)', background:'repeating-linear-gradient(45deg,rgba(245,166,35,.03),rgba(245,166,35,.03) 10px,transparent 10px,transparent 20px)' }}>
              <div style={{ display:'flex', gap:12 }}>
                <AlertTriangle size={20} color="var(--amber)" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'var(--amber)', marginBottom:4 }}>Demo submission only</div>
                  <div style={{ fontSize:13, color:'var(--slate)' }}>Nothing will be sent to HMRC. Connect your account in Settings to file for real.</div>
                </div>
              </div>
            </div>
          )}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>You are about to submit</div>
            {[
              { label:'Period',     value:`${activeQuarterKey} · ${activeQuarterPeriod?.label}` },
              { label:'Income',     value:fmt(income) },
              { label:'Expenses',   value:fmt(totalExp) },
              { label:'Net profit', value:fmt(qProfit) },
              { label:'Income Tax', value:fmt(incomeTax) },
              { label:'NI due',     value:fmt(ni) },
              { label:'Total tax',  value:fmt(total) },
            ].map(r => (
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize:12, color:'var(--slate)' }}>{r.label}</span>
                <span style={{ fontSize:12, color:'var(--white)', fontWeight:500 }}>{r.value}</span>
              </div>
            ))}
          </div>
          <div className="alert" style={{ marginBottom:16, background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.2)', color:'var(--teal)' }}>
            <Lock size={14} />
            <span style={{ fontSize:12 }}>Submitting will lock {activeQuarterKey}. Transactions in this period will be protected — any edits tracked as amendments.</span>
          </div>
          {submitError && (
            <div className="alert" style={{ marginBottom:16, background:'rgba(255,92,92,.08)', border:'1px solid rgba(255,92,92,.3)' }}>
              <XCircle size={14} color="var(--red)" />
              <span style={{ fontSize:12, color:'var(--red)' }}>{submitError}</span>
            </div>
          )}
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-ghost btn-full" onClick={() => setStep(1)} disabled={submitting}>Back</button>
            <button
              className="btn btn-primary btn-full btn-lg"
              onClick={hmrcConnected ? submitToHMRC : submitDemo}
              disabled={submitting || uncatCount > 0}>
              {submitting
                ? <><span className="spin" style={{ width:14, height:14 }} /> Submitting...</>
                : uncatCount > 0
                  ? <>Categorise {uncatCount} transaction{uncatCount === 1 ? '' : 's'} first</>
                  : <><Send size={15} /> {hmrcConnected ? `Submit ${activeQuarterKey} to HMRC` : `Submit ${activeQuarterKey} (demo)`}</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
